import React from 'react';
import { Stack, Typography } from '@mui/material';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute.tsx';
import TenantsListPage from './pages/TenantsListPage.tsx';
import TenantDetailPage from './pages/TenantDetailPage.tsx';
import TenantMediaPage from './pages/TenantMediaPage.tsx';
import UsersPage from './pages/UsersPage.tsx';
import LogsPage from './pages/LogsPage.tsx';
import LocationsPage from './pages/LocationsPage.tsx';
import SpacesPage from './pages/SpacesPage.tsx';
import TablesPage from './pages/TablesPage.tsx';
import ReservationsPage from './pages/ReservationsPage.tsx';
import AcceptInvitePage from './pages/AcceptInvitePage.tsx';
import LoginPage from './pages/LoginPage.tsx';
import SearchPage from './pages/SearchPage.tsx';
import WaitlistPage from './pages/WaitlistPage.tsx';
import Dashboard from './pages/Dashboard.tsx';
import ReportsPage from './pages/ReportsPage.tsx';
import HotelsPage from './pages/hotels/HotelsPage.tsx';
import HotelForm from './pages/hotels/HotelForm.tsx';
import RoomsPage from './pages/RoomsPage.tsx';
import RoomTypesPage from './pages/RoomTypesPage.tsx';
import HousekeepingBoard from './pages/HousekeepingBoard.tsx';
import ShiftsMonitor from './pages/ShiftsMonitor.tsx';
import InventoryAudit from './pages/InventoryAudit.tsx';
import InventoryOverview from './pages/InventoryOverview.tsx';
import InventoryItemsPage from './pages/InventoryItemsPage.tsx';
import InventoryBatchesPage from './pages/InventoryBatchesPage.tsx';
import AccessDirectoryPage from './pages/AccessDirectoryPage.tsx';
import LocationTeamPage from './pages/LocationTeamPage.tsx';
import PermissionProfilesPage from './pages/PermissionProfilesPage.tsx';
import SupportActionsPage from './pages/SupportActionsPage.tsx';
import DemoMatrixPage from './pages/DemoMatrixPage.tsx';
import FeatureFlagsPage from './pages/FeatureFlagsPage.tsx';
import FiscalHealthPage from './pages/FiscalHealthPage.tsx';
import OfflineQueuesPage from './pages/OfflineQueuesPage.tsx';
import { toastError } from './shared/ui/toast.ts';

const guard = (
  element: JSX.Element,
  requiredPermissions: string[] = [],
  requiredRoles: string[] = [],
) => (
  <PrivateRoute
    requiredPermissions={requiredPermissions}
    requiredRoles={requiredRoles}
  >
    {element}
  </PrivateRoute>
);

const DashboardNotFound: React.FC = () => (
  <Stack spacing={1.5}>
    <Typography variant="overline" color="text.secondary">
      Routing
    </Typography>
    <Typography variant="h4">Page not found</Typography>
    <Typography color="text.secondary">
      Ruta ceruta nu exista in consola interna 3100.
    </Typography>
  </Stack>
);

const DashboardRoutes: React.FC = () => (
  <Routes>
    <Route
      index
      element={guard(<Dashboard />, ['support.web.dashboard:VIEW'])}
    />
    <Route
      path="tenants"
      element={guard(<TenantsListPage />, ['support.web.tenants:VIEW'])}
    />
    <Route
      path="tenants/:id"
      element={guard(<TenantDetailPage />, ['support.web.tenants:VIEW'])}
    />
    <Route
      path="tenants/:tenantId/media"
      element={guard(<TenantMediaPage />, ['support.web.tenants:VIEW'])}
    />
    <Route
      path="tenants/:tenantId/waitlist"
      element={guard(<WaitlistPage />, ['support.web.waitlist:VIEW'])}
    />
    <Route
      path="users"
      element={guard(<UsersPage />, ['support.web.users:VIEW'])}
    />
    <Route
      path="locations"
      element={guard(<LocationsPage />, ['support.web.locations:VIEW'])}
    />
    <Route
      path="reservations"
      element={guard(<ReservationsPage />, ['support.web.reservations:VIEW'])}
    />
    <Route
      path="logs"
      element={guard(<LogsPage />, ['support.web.logs:VIEW'])}
    />
    <Route
      path="access-directory"
      element={guard(<AccessDirectoryPage />, ['support.web.access_directory:VIEW'])}
    />
    <Route
      path="location-team"
      element={guard(<LocationTeamPage />, ['support.web.location_team:VIEW'])}
    />
    <Route
      path="permission-profiles"
      element={guard(<PermissionProfilesPage />, ['support.web.permission_profiles:VIEW'])}
    />
    <Route
      path="support-actions"
      element={guard(<SupportActionsPage />, ['support.web.support_actions:VIEW'])}
    />
    <Route
      path="spaces"
      element={guard(<SpacesPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="tables"
      element={guard(<TablesPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="reports"
      element={guard(<ReportsPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="hotels"
      element={guard(<HotelsPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="hotels/:id"
      element={guard(<HotelForm />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="hotel-rooms"
      element={guard(<RoomsPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="hotel-room-types"
      element={guard(<RoomTypesPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="hotel-housekeeping"
      element={guard(<HousekeepingBoard />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="shifts-monitor"
      element={guard(<ShiftsMonitor />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="inventory-audit"
      element={guard(<InventoryAudit />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="inventory-overview"
      element={guard(<InventoryOverview />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="inventory/items"
      element={guard(<InventoryItemsPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="inventory/batches"
      element={guard(<InventoryBatchesPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="demo-matrix"
      element={guard(<DemoMatrixPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="feature-flags"
      element={guard(<FeatureFlagsPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="fiscal-health"
      element={guard(<FiscalHealthPage />, [], ['SUPER_ADMIN'])}
    />
    <Route
      path="offline-queues"
      element={guard(<OfflineQueuesPage />, [], ['SUPER_ADMIN'])}
    />
    <Route path="*" element={<DashboardNotFound />} />
  </Routes>
);

const AcceptInviteAliasRedirect: React.FC = () => {
  const { token } = useParams();
  return <Navigate to={`/invite/${token || ''}`} replace />;
};

const LegacySuperadminRedirect: React.FC = () => {
  const location = useLocation();
  // SUPERADMIN-12 (MA-28c): the legacy /superadmin/* path is remapped to /dashboard/* — announce the
  // move once with a visible notice instead of swapping the URL silently.
  React.useEffect(() => {
    toastError('The /superadmin path has moved to /dashboard — redirecting.');
  }, []);
  const path = location.pathname.replace(/^\/superadmin/, '') || '/';
  const suffix = `${location.search}${location.hash}`;

  if (path === '/' || path === '') {
    return <Navigate to={`/dashboard${suffix}`} replace />;
  }

  if (path === '/login') {
    return <Navigate to={`/login${suffix}`} replace />;
  }

  if (path.startsWith('/invite/')) {
    return <Navigate to={`${path}${suffix}`} replace />;
  }

  if (path.startsWith('/accept-invite/')) {
    return (
      <Navigate
        to={`${path.replace('/accept-invite/', '/invite/')}${suffix}`}
        replace
      />
    );
  }

  const target = path.startsWith('/dashboard') ? path : `/dashboard${path}`;
  return <Navigate to={`${target}${suffix}`} replace />;
};

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<Navigate to="/dashboard" replace />} />
    <Route path="/superadmin/*" element={<LegacySuperadminRedirect />} />
    <Route path="/accept-invite/:token" element={<AcceptInviteAliasRedirect />} />

    <Route
      path="/dashboard/*"
      element={
        <PrivateRoute>
          <DashboardRoutes />
        </PrivateRoute>
      }
    />

    <Route path="/login" element={<LoginPage />} />
    <Route path="/invite/:token" element={<AcceptInvitePage />} />
    <Route path="/search" element={<PrivateRoute><SearchPage /></PrivateRoute>} />
    <Route path="*" element={<Typography variant="h6">Page not found</Typography>} />
  </Routes>
);

export default AppRoutes;
